import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SearchIcon from '@mui/icons-material/Search';
import { cn } from '@/utils/cn';
import type { SelectOption } from '@/components/ui/Select';
import { FieldLabel, parseFieldLabel } from '@/components/ui/FieldLabel';
import { useDebounce } from '@/hooks/useDebounce';
import { FloatingListbox } from '@/components/ui/FloatingListbox';
import { normalizeSelectOptions } from '@/components/ui/selectOptionUtils';

export interface SearchableSelectProps {
  label?: string;
  ariaLabel?: string;
  error?: string;
  hint?: string;
  options: SelectOption[];
  placeholder?: string;
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  loading?: boolean;
  /** True while a server search request is in flight. */
  searching?: boolean;
  className?: string;
  id?: string;
  name?: string;
  required?: boolean;
  searchPlaceholder?: string;
  emptyMessage?: string;
  /** Filter options in the dropdown locally (default true). Set false when options come from the server. */
  filterLocally?: boolean;
  /** Called with debounced search text — use for backend search. */
  onSearchQuery?: (query: string) => void;
  searchDebounceMs?: number;
  /** When true, dropdown uses header overlay stacking (above sidebar). */
  headerOverlay?: boolean;
  clearLabel?: string;
  /** Show search input in the dropdown (default true). */
  showSearch?: boolean;
  /** Allow clearing the selection with an inline X on the trigger (default: not required). */
  allowClear?: boolean;
}

export function SearchableSelect({
  label,
  ariaLabel,
  error,
  hint: _hint,
  options,
  placeholder: placeholderProp,
  value = '',
  onChange,
  disabled,
  loading,
  searching = false,
  className,
  id,
  name,
  required,
  searchPlaceholder: searchPlaceholderProp,
  emptyMessage: emptyMessageProp,
  filterLocally = true,
  onSearchQuery,
  searchDebounceMs = 300,
  headerOverlay = false,
  clearLabel,
  showSearch,
  allowClear,
}: SearchableSelectProps) {
  const { t } = useTranslation('common');
  const placeholder = placeholderProp ?? t('actions.select');
  const searchPlaceholder = searchPlaceholderProp ?? t('picker.search');
  const emptyMessage = emptyMessageProp ?? t('picker.noMatches');
  const autoId = useId();
  const fieldId = id ?? name ?? autoId;
  const { required: isRequired } = parseFieldLabel(label, required);
  const anchorRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const onSearchQueryRef = useRef(onSearchQuery);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  const debouncedQuery = useDebounce(query, searchDebounceMs);
  const serverSearch = Boolean(onSearchQuery);
  const safeOptions = useMemo(() => normalizeSelectOptions(options), [options]);
  const searchEnabled = showSearch ?? (serverSearch || safeOptions.length >= 10);
  const canClear = allowClear ?? !isRequired;
  const showClearButton = canClear && value !== '' && !disabled && !loading;

  const selected = safeOptions.find((o) => o.value === value);
  const displayLabel = selected?.label ?? (loading ? 'Loading…' : placeholder);

  useEffect(() => {
    onSearchQueryRef.current = onSearchQuery;
  }, [onSearchQuery]);

  useEffect(() => {
    if (!serverSearch) return;
    onSearchQueryRef.current?.(debouncedQuery);
  }, [debouncedQuery, serverSearch]);

  const filtered = useMemo(() => {
    if (!filterLocally || serverSearch) return safeOptions;
    const q = query.trim().toLowerCase();
    if (!q) return safeOptions;
    return safeOptions.filter(
      (opt) => opt.label.toLowerCase().includes(q) || opt.value.toLowerCase().includes(q),
    );
  }, [safeOptions, query, filterLocally, serverSearch]);

  const enabledOptions = useMemo(() => filtered.filter((opt) => !opt.disabled), [filtered]);
  const activeOption = activeIndex >= 0 ? enabledOptions[activeIndex] : undefined;
  const listboxId = `${fieldId}-listbox`;
  const errorId = `${fieldId}-error`;
  const optionId = (optionValue: string) =>
    `${listboxId}-option-${encodeURIComponent(optionValue).replaceAll('%', '-')}`;

  useEffect(() => {
    if (!open) {
      setQuery('');
      setActiveIndex(-1);
      return;
    }
    const selectedIndex = enabledOptions.findIndex((option) => option.value === value);
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : enabledOptions.length > 0 ? 0 : -1);
    if (searchEnabled) {
      window.setTimeout(() => searchRef.current?.focus(), 0);
    }
  }, [enabledOptions, open, searchEnabled, value]);

  useEffect(() => {
    if (!open || activeIndex < 0) return;
    const activeValue = enabledOptions[activeIndex]?.value ?? '';
    document
      .getElementById(
        `${listboxId}-option-${encodeURIComponent(activeValue).replaceAll('%', '-')}`,
      )
      ?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, enabledOptions, listboxId, open]);

  const pick = (next: string) => {
    onChange?.(next);
    setOpen(false);
    window.setTimeout(() => triggerRef.current?.focus(), 0);
  };

  const handleClear = (e: ReactMouseEvent) => {
    e.stopPropagation();
    onChange?.('');
    setOpen(false);
  };

  const toggleOpen = () => {
    if (!disabled && !loading) setOpen((o) => !o);
  };

  const handleKeyDown = (event: ReactKeyboardEvent) => {
    if (event.key === 'Escape' && open) {
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
      triggerRef.current?.focus();
      return;
    }
    if (event.key === 'Tab') {
      setOpen(false);
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      if (enabledOptions.length === 0) return;
      setActiveIndex((current) => {
        const start = current < 0 ? (event.key === 'ArrowDown' ? -1 : 0) : current;
        return event.key === 'ArrowDown'
          ? (start + 1) % enabledOptions.length
          : (start - 1 + enabledOptions.length) % enabledOptions.length;
      });
      return;
    }
    if (event.key === 'Home' && open) {
      event.preventDefault();
      setActiveIndex(enabledOptions.length > 0 ? 0 : -1);
      return;
    }
    if (event.key === 'End' && open) {
      event.preventDefault();
      setActiveIndex(enabledOptions.length - 1);
      return;
    }
    if (
      (event.key === 'Enter' ||
        (event.key === ' ' && event.currentTarget === triggerRef.current)) &&
      open &&
      activeOption
    ) {
      event.preventDefault();
      pick(activeOption.value);
    }
  };

  return (
    <div className={cn('min-w-0 w-full', className)}>
      <FieldLabel htmlFor={fieldId} label={label} required={required} />
      <div
        ref={anchorRef}
        className={cn(
          'input flex w-full min-w-0 items-center gap-1 p-0',
          error && 'input-error',
          (disabled || loading) && 'cursor-not-allowed opacity-60',
        )}
      >
        <button
          ref={triggerRef}
          type="button"
          id={fieldId}
          disabled={disabled || loading}
          role="combobox"
          aria-haspopup="listbox"
          aria-label={ariaLabel}
          aria-expanded={open}
          aria-controls={open ? listboxId : undefined}
          aria-activedescendant={
            open && activeOption ? optionId(activeOption.value) : undefined
          }
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          onClick={toggleOpen}
          onKeyDown={handleKeyDown}
          className={cn(
            'flex min-w-0 flex-1 items-center px-3 py-2 text-left',
            !selected && 'text-slate-500 dark:text-slate-400',
            selected && 'text-slate-900 dark:text-white',
            (disabled || loading) && 'cursor-not-allowed',
          )}
        >
          <span className="truncate">{displayLabel}</span>
        </button>
        {showClearButton && (
          <button
            type="button"
            aria-label={t('labels.clearSelection')}
            onClick={handleClear}
            className="shrink-0 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-700 dark:hover:text-slate-200"
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </button>
        )}
        <button
          type="button"
          disabled={disabled || loading}
          aria-label={open ? 'Close options' : 'Open options'}
          onClick={toggleOpen}
          className={cn(
            'shrink-0 px-2 py-2 text-slate-500 dark:text-slate-400',
            (disabled || loading) && 'cursor-not-allowed',
          )}
        >
          <KeyboardArrowDownIcon
            className={cn('transition-transform', open && 'rotate-180')}
            fontSize="small"
          />
        </button>
      </div>
      {name && <input type="hidden" name={name} value={value} required={isRequired} readOnly />}
      <FloatingListbox
        open={open}
        onOpenChange={setOpen}
        anchorRef={anchorRef}
        id={`${listboxId}-popover`}
        className={headerOverlay ? 'workspace-header-overlay' : undefined}
      >
          {searchEnabled && (
            <div className="border-b border-slate-100 p-2 dark:border-slate-700">
              <div className="relative">
                <SearchIcon
                  className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400"
                  sx={{ fontSize: 18 }}
                />
                <input
                  ref={searchRef}
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={searchPlaceholder}
                  className="input w-full py-1.5 pl-8 pr-2 text-sm"
                  aria-label={searchPlaceholder}
                  role="combobox"
                  aria-expanded={open}
                  aria-controls={listboxId}
                  aria-activedescendant={
                    activeOption ? optionId(activeOption.value) : undefined
                  }
                  onKeyDown={handleKeyDown}
                />
              </div>
            </div>
          )}
          <ul
            id={listboxId}
            role="listbox"
            aria-labelledby={fieldId}
            className="min-h-0 flex-1 overflow-y-auto py-1"
          >
            {clearLabel && (
              <li role="option">
                <button
                  type="button"
                  className={cn(
                    'w-full px-3 py-2 text-left text-sm text-slate-500 hover:bg-slate-50 dark:text-slate-400 dark:hover:bg-slate-700',
                    value === '' && 'bg-primary-50 font-medium text-primary-700 dark:bg-primary-950/50 dark:text-primary-300',
                  )}
                  onClick={() => pick('')}
                >
                  {clearLabel}
                </button>
              </li>
            )}
            {canClear && placeholder && !clearLabel && (
              <li role="option">
                <button
                  type="button"
                  className={cn(
                    'w-full px-3 py-2 text-left text-sm text-slate-500 hover:bg-slate-50 dark:text-slate-400 dark:hover:bg-slate-700',
                    value === '' && 'bg-primary-50 font-medium text-primary-700 dark:bg-primary-950/50 dark:text-primary-300',
                  )}
                  onClick={() => pick('')}
                >
                  {placeholder}
                </button>
              </li>
            )}
            {filtered.length === 0 && (
              <li
                role="status"
                aria-live="polite"
                className="px-3 py-2 text-sm text-slate-500 dark:text-slate-400"
              >
                {searching ? 'Searching…' : emptyMessage}
              </li>
            )}
            {filtered.map((opt) => (
              <li
                key={opt.value}
                id={optionId(opt.value)}
                role="option"
                aria-selected={value === opt.value}
                aria-disabled={opt.disabled || undefined}
              >
                <button
                  type="button"
                  disabled={opt.disabled}
                  className={cn(
                    'w-full px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:text-slate-100 dark:hover:bg-slate-700',
                    value === opt.value &&
                      'bg-primary-50 font-medium text-primary-700 dark:bg-primary-950/50 dark:text-primary-300',
                    activeIndex >= 0 &&
                      enabledOptions[activeIndex]?.value === opt.value &&
                      'bg-slate-100 dark:bg-slate-700',
                  )}
                  onClick={() => !opt.disabled && pick(opt.value)}
                >
                  {opt.label}
                </button>
              </li>
            ))}
          </ul>
      </FloatingListbox>
      {error && <p id={errorId} className="mt-1 text-xs text-red-600" role="alert">{error}</p>}
    </div>
  );
}
